import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";

// Pages (eager: critiques pour SEO + SSG)
import HomePage from "@/pages/HomePage";
import PhoneRepairPage from "@/pages/PhoneRepairPage";
import ComputerRepairPage from "@/pages/ComputerRepairPage";
import ProfessionalServicesPage from "@/pages/ProfessionalServicesPage";
import ReviewsPage from "@/pages/ReviewsPage";
import ContactPage from "@/pages/ContactPage";
import EmergencyPage from "@/pages/EmergencyPage";
import GarantiePage from "@/pages/GarantiePage";
import AppointmentPage from "@/pages/AppointmentPage";
import ClickCollectPage from "@/pages/ClickCollectPage";

// Lazy: pas indexé / hors parcours principal
const AdminPage = lazy(() => import("@/pages/AdminPage"));

// Nouvelles landing pages SEO (mots-clés locaux Niort)
import ReparationIphoneNiortPage from "@/pages/ReparationIphoneNiortPage";
import RepriseIphoneNiortPage from "@/pages/RepriseIphoneNiortPage";
import IphoneReconditionneNiortPage from "@/pages/IphoneReconditionneNiortPage";
import ReparationMacNiortPage from "@/pages/ReparationMacNiortPage";
import ReparationIpadNiortPage from "@/pages/ReparationIpadNiortPage";
import ReparationMultimarqueNiortPage from "@/pages/ReparationMultimarqueNiortPage";

// Blog
import BlogPage from "@/pages/BlogPage";
import BlogArticlePage from "@/pages/BlogArticlePage";

// Pages villes desservies (SEO local - rayon 80 km Niort)
import CityPage from "@/pages/CityPage";
import VillesDesserviesPage from "@/pages/VillesDesserviesPage";
import { CITIES } from "@/data/citiesData";

// Pages pannes spécifiques (SEO longue traîne - Phase B)
import PannePage from "@/pages/PannePage";
import PannesIndexPage from "@/pages/PannesIndexPage";
import { PANNES } from "@/data/pannesData";

// Pages combinées Panne × Ville (SEO ultra-longue traîne - Phase C)
import PanneVillePage from "@/pages/PanneVillePage";
import ReparationsLocalesPage from "@/pages/ReparationsLocalesPage";
import { ALL_COMBOS } from "@/data/panneVilleData";

// Phase G/H/I — DOMINATION IA (LLM SEO) : Guides, Études de cas, Comparatifs
import GuidePage from "@/pages/GuidePage";
import GuidesIndexPage from "@/pages/GuidesIndexPage";
import { GUIDES } from "@/data/guidesData";
import CaseStudyPage from "@/pages/CaseStudyPage";
import CaseStudiesIndexPage from "@/pages/CaseStudiesIndexPage";
import { CASE_STUDIES } from "@/data/caseStudiesData";
import ComparisonPage from "@/pages/ComparisonPage";
import ComparisonsIndexPage from "@/pages/ComparisonsIndexPage";
import { COMPARISONS } from "@/data/comparisonsData";
import AiHubPage from "@/pages/AiHubPage";

// Pages légales + 404
import MentionsLegalesPage from "@/pages/MentionsLegalesPage";
import PolitiqueConfidentialitePage from "@/pages/PolitiqueConfidentialitePage";
import NotFoundPage from "@/pages/NotFoundPage";

// Components
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { StickyMobileBar } from "@/components/StickyMobileBar";
import { WhatsAppButton } from "@/components/WhatsAppButton";
import { ChatbotWidget } from "@/components/ChatbotWidget";

// Layout wrapper that conditionally renders Header/Footer
function Layout({ children }) {
  const location = useLocation();
  const isAdminPage = location.pathname.startsWith("/admin");
  
  if (isAdminPage) {
    return <>{children}</>;
  }
  
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1 mobile-bottom-padding">
        {children}
      </main>
      <Footer />
      <StickyMobileBar />
      <WhatsAppButton />
      <ChatbotWidget />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <Layout>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/reparation-telephone" element={<PhoneRepairPage />} />
          <Route path="/reparation-ordinateur" element={<ComputerRepairPage />} />
          <Route path="/services-professionnels" element={<ProfessionalServicesPage />} />
          <Route path="/avis-clients" element={<ReviewsPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/urgence" element={<EmergencyPage />} />
          <Route path="/garantie" element={<GarantiePage />} />
          <Route path="/rendez-vous" element={<AppointmentPage />} />
          <Route path="/click-collect" element={<ClickCollectPage />} />
          {/* Landing pages SEO */}
          <Route path="/reparation-iphone-niort" element={<ReparationIphoneNiortPage />} />
          <Route path="/reprise-iphone-niort" element={<RepriseIphoneNiortPage />} />
          <Route path="/iphone-reconditionne-niort" element={<IphoneReconditionneNiortPage />} />
          <Route path="/reparation-mac-niort" element={<ReparationMacNiortPage />} />
          <Route path="/reparation-ipad-niort" element={<ReparationIpadNiortPage />} />
          <Route path="/reparation-multimarque-niort" element={<ReparationMultimarqueNiortPage />} />
          {/* Blog */}
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:slug" element={<BlogArticlePage />} />
          {/* Pages villes - SEO local rayon 80 km (routes générées) */}
          <Route path="/villes-desservies" element={<VillesDesserviesPage />} />
          {CITIES.map((c) => (
            <Route
              key={c.slug}
              path={`/reparation-${c.slug}`}
              element={<CityPage />}
            />
          ))}
          {/* Pages pannes - SEO longue traîne (routes générées) */}
          <Route path="/pannes-reparations" element={<PannesIndexPage />} />
          {PANNES.map((p) => (
            <Route
              key={p.slug}
              path={`/${p.slug}`}
              element={<PannePage />}
            />
          ))}
          {/* Pages combinées panne × ville - 216 routes ultra-longue traîne (Phase C) */}
          <Route path="/reparations-locales" element={<ReparationsLocalesPage />} />
          {ALL_COMBOS.map((c) => (
            <Route
              key={c.slug}
              path={`/${c.slug}`}
              element={<PanneVillePage />}
            />
          ))}
          {/* Phase G/H/I - LLM SEO : Guides (30), Études de cas (20), Comparatifs (10) */}
          <Route path="/guides" element={<GuidesIndexPage />} />
          {GUIDES.map((g) => (
            <Route key={g.slug} path={`/guides/${g.slug}`} element={<GuidePage />} />
          ))}
          <Route path="/etudes-de-cas" element={<CaseStudiesIndexPage />} />
          {CASE_STUDIES.map((c) => (
            <Route key={c.slug} path={`/etudes-de-cas/${c.slug}`} element={<CaseStudyPage />} />
          ))}
          <Route path="/comparatifs" element={<ComparisonsIndexPage />} />
          {COMPARISONS.map((c) => (
            <Route key={c.slug} path={`/comparatifs/${c.slug}`} element={<ComparisonPage />} />
          ))}
          {/* AI Hub — Centre d'information IA pour LLM (Phase X10000) */}
          <Route path="/ia-niort" element={<AiHubPage />} />
          {/* Pages légales */}
          <Route path="/mentions-legales" element={<MentionsLegalesPage />} />
          <Route path="/politique-de-confidentialite" element={<PolitiqueConfidentialitePage />} />
          <Route
            path="/admin"
            element={
              <Suspense fallback={<div className="min-h-screen bg-slate-900" />}>
                <AdminPage />
              </Suspense>
            }
          />
          {/* Catch-all 404 : évite tout rendu vide sur URL inconnue */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Layout>
      <Toaster position="top-right" richColors />
    </BrowserRouter>
  );
}

export default App;
